import React, { useState, useEffect, useRef } from 'react';

interface PuzzleGameProps {
  gridSize: number; // 4, 6, or 8 pieces
  duration: number;
  onComplete: (timeSpent: number, moves: number) => void;
}

interface ColorPiece {
  id: number;
  hue: number;
  lightness: number;
  originalOrder: number;
}

const PuzzleGame: React.FC<PuzzleGameProps> = ({ gridSize, duration, onComplete }) => {
  const [pieces, setPieces] = useState<ColorPiece[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [moves, setMoves] = useState(0);
  const [isSolved, setIsSolved] = useState(false);
  const [baseHue, setBaseHue] = useState(200);
  const [elapsedTime, setElapsedTime] = useState(0);
  const startTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    // Generate color pieces with different lightness values
    // Using more saturated and distinguishable hues
    const hues = [210, 0, 120, 270, 45, 180]; // Blue, Red, Green, Purple, Orange, Cyan - more distinct
    const randomHue = hues[Math.floor(Math.random() * hues.length)];
    setBaseHue(randomHue);

    // Create pieces with varying lightness (darkest to lightest)
    // Increased range from 15% to 85% for better distinction
    const initialPieces: ColorPiece[] = Array.from({ length: gridSize }, (_, i) => ({
      id: i,
      hue: randomHue,
      lightness: 15 + (i * (70 / (gridSize - 1))), // Range from 15% to 85% for better contrast
      originalOrder: i,
    }));

    // Shuffle the pieces until they're not in order
    const shuffled = [...initialPieces];
    let isSortedAlready = true;
    while (isSortedAlready) {
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      // Check if shuffled result is still sorted
      isSortedAlready = shuffled.every((piece, index) => piece.originalOrder === index);
    }
    setPieces(shuffled);
  }, [gridSize]);

  // Timer effect
  useEffect(() => {
    if (isSolved) return;
    
    const timer = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [isSolved]);

  useEffect(() => {
    // Check if puzzle is solved (pieces ordered from darkest to lightest)
    if (pieces.length === 0) return;
    
    const solved = pieces.every((piece, index) => piece.originalOrder === index);
    if (solved && moves > 0) {
      setIsSolved(true);
      const finalTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setTimeout(() => onComplete(finalTime, moves), 1000);
    }
  }, [pieces, moves, onComplete]);

  const handlePieceClick = (index: number) => {
    if (isSolved) return;

    if (selectedIndex === null) {
      setSelectedIndex(index);
    } else {
      // Swap pieces
      const newPieces = [...pieces];
      [newPieces[selectedIndex], newPieces[index]] = [newPieces[index], newPieces[selectedIndex]];
      setPieces(newPieces);
      setSelectedIndex(null);
      setMoves(moves + 1);
    }
  };

  const handleShuffle = () => {
    const shuffled = [...pieces];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setPieces(shuffled);
    setSelectedIndex(null);
    setMoves(moves + 1);
  };

  const getGridCols = () => {
    if (gridSize === 4) return 'grid-cols-4';
    if (gridSize === 6) return 'grid-cols-6';
    return 'grid-cols-8';
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5">
        <h3 className="font-bold text-blue-800 text-sm mb-2">Comment jouer :</h3>
        <ul className="text-blue-700 text-sm space-y-1">
          <li>• Organisez les couleurs du plus foncé au plus clair</li>
          <li>• Cliquez sur une couleur pour la sélectionner</li>
          <li>• Cliquez sur une autre pour les échanger</li>
        </ul>
      </div>

      {/* Progress indicator */}
      <div className="mb-4 flex justify-between items-center text-sm">
        <span className="text-gray-600">
          Échanges: <span className="font-bold text-primary">{moves}</span>
        </span>
        <span className="text-gray-600">
          Temps: <span className="font-bold text-primary">{Math.floor(elapsedTime / 60)}:{(elapsedTime % 60).toString().padStart(2, '0')}</span>
        </span>
        {isSolved && (
          <span className="text-green-600 font-bold">Bravo!</span>
        )}
      </div>

      {/* Direction indicator */}
      <div className="flex justify-between items-center mb-3 text-sm text-gray-500">
        <span className="font-medium">Foncé</span>
        <span className="text-lg">→</span>
        <span className="font-medium">Clair</span>
      </div>

      {/* Color pieces grid */}
      <div className={`grid ${getGridCols()} gap-3 mb-4`}>
        {pieces.map((piece, index) => (
          <div
            key={piece.id}
            onClick={() => handlePieceClick(index)}
            className={`
              aspect-square rounded-lg cursor-pointer transition-all duration-200
              ${selectedIndex === index 
                ? 'ring-4 ring-yellow-400 scale-95 shadow-xl' 
                : 'shadow-lg hover:shadow-xl hover:scale-105'
              }
              ${isSolved ? 'animate-pulse' : ''}
            `}
            style={{
              backgroundColor: `hsl(${piece.hue}, 80%, ${piece.lightness}%)`,
              minHeight: '55px',
              border: '2px solid rgba(0,0,0,0.1)'
            }}
          >
            {selectedIndex === index && (
              <div className="w-full h-full flex items-center justify-center">
                <div className="w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PuzzleGame;